import { useEffect, useState, useCallback, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { RefreshCw, Search, X } from "lucide-react";
import { formatINR } from "@/lib/format";

export const GroupStatsPanel = () => {
  const [rows, setRows] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set());

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get("/admin/stats/by-group");
      setRows(data.rows || []);
    } catch {
      /* transient */
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  const searchFiltered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.group.toLowerCase().includes(q));
  }, [rows, query]);

  const toggleSelect = useCallback((group) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      searchFiltered.forEach((r) => next.add(r.group));
      return next;
    });
  }, [searchFiltered]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  if (!rows) return null;

  // Total row reflects: selected rows if any; else all currently-visible (search-filtered) rows.
  const totalSourceRows =
    selected.size > 0 ? rows.filter((r) => selected.has(r.group)) : searchFiltered;
  const totals = totalSourceRows.reduce(
    (acc, r) => ({
      billed: acc.billed + r.total_billed,
      pending: acc.pending + r.total_pending,
      paid: acc.paid + r.total_paid,
    }),
    { billed: 0, pending: 0, paid: 0 }
  );
  let totalLabel = "Total";
  if (selected.size > 0) totalLabel = `Selected (${selected.size})`;
  else if (query.trim()) totalLabel = "Filtered total";

  return (
    <Card
      className="border-slate-200 shadow-none rounded-md p-5 mb-6"
      data-testid="group-stats-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="label-tag">Turnover & Dues</p>
          <h3 className="font-display text-lg font-medium text-slate-900 mt-1">
            By Group
          </h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => load(false)}
          disabled={refreshing}
          className="border-slate-300"
          data-testid="group-stats-refresh"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No invoices yet.</p>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search group…"
                className="pl-7 pr-8 h-8 text-sm rounded-md border-slate-300"
                data-testid="group-stats-search"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  data-testid="group-stats-search-clear"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs border-slate-300"
              onClick={selectAllVisible}
              disabled={searchFiltered.length === 0}
              data-testid="group-stats-select-all"
            >
              Select all
            </Button>
            {selected.size > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs border-slate-300"
                onClick={clearSelection}
                data-testid="group-stats-clear-selection"
              >
                Clear ({selected.size})
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="group-stats-table">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-2 w-8"></th>
                  <th className="py-2 font-medium">Group</th>
                  <th className="py-2 font-medium text-right">Total Billed</th>
                  <th className="py-2 font-medium text-right">Paid</th>
                  <th className="py-2 font-medium text-right">Pending</th>
                </tr>
              </thead>
              <tbody>
                {searchFiltered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-3 text-center text-slate-400 italic text-xs">
                      No groups match “{query}”.
                    </td>
                  </tr>
                )}
                {searchFiltered.map((r) => {
                  const isSel = selected.has(r.group);
                  return (
                    <tr
                      key={r.group}
                      onClick={() => toggleSelect(r.group)}
                      className={`border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 ${isSel ? "bg-slate-50" : ""}`}
                      data-testid={`group-row-${r.group.replace(/\s+/g, "-")}`}
                    >
                      <td className="py-2">
                        <Checkbox
                          checked={isSel}
                          onCheckedChange={() => toggleSelect(r.group)}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`group-select-${r.group.replace(/\s+/g, "-")}`}
                        />
                      </td>
                      <td className="py-2 font-medium text-slate-900">{r.group}</td>
                      <td className="py-2 text-right font-mono">
                        {formatINR(r.total_billed)}
                      </td>
                      <td className="py-2 text-right font-mono text-emerald-700">
                        {formatINR(r.total_paid)}
                      </td>
                      <td className="py-2 text-right font-mono text-rose-700">
                        {formatINR(r.total_pending)}
                      </td>
                    </tr>
                  );
                })}
                {totalSourceRows.length > 0 && (
                  <tr className="border-t-2 border-slate-300 font-semibold" data-testid="group-row-total">
                    <td className="py-2"></td>
                    <td className="py-2">{totalLabel}</td>
                    <td className="py-2 text-right font-mono">
                      {formatINR(totals.billed)}
                    </td>
                    <td className="py-2 text-right font-mono text-emerald-700">
                      {formatINR(totals.paid)}
                    </td>
                    <td className="py-2 text-right font-mono text-rose-700">
                      {formatINR(totals.pending)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
};
